import { thumbUrl } from "../lib/photos";

export type AvatarSize = "xs" | "sm" | "md" | "lg" | "xl";

// px is the CSS size; the rendition is requested at 2x for hidpi screens.
const SIZES: Record<AvatarSize, { px: number; box: string; text: string }> = {
  xs: { px: 16, box: "size-4", text: "text-[9px]" },
  sm: { px: 24, box: "size-6", text: "text-xs" },
  md: { px: 32, box: "size-8", text: "text-sm" },
  lg: { px: 48, box: "size-12", text: "text-lg" },
  xl: { px: 80, box: "size-20", text: "text-2xl" },
};

/**
 * Person avatar: a square smart-cropped imagor rendition when `avatarKey` is
 * set, otherwise an initial-letter circle. Spots that can't reach avatarKey
 * cheaply may omit it and get the initials fallback. `className` is for
 * positioning only (margins, alignment) — sizing comes from `size`.
 */
export function Avatar({
  person,
  size = "md",
  className = "",
}: {
  person: { displayName: string | null; avatarKey?: string | null };
  size?: AvatarSize;
  className?: string;
}) {
  const s = SIZES[size];
  if (person.avatarKey) {
    return (
      <span
        aria-hidden
        className={`inline-block shrink-0 overflow-hidden rounded-full border ${s.box} ${className}`}
      >
        <img
          src={thumbUrl(person.avatarKey, s.px * 2, s.px * 2)}
          alt=""
          loading="lazy"
          width={s.px}
          height={s.px}
          className="size-full object-cover"
        />
      </span>
    );
  }
  const initial = person.displayName?.trim()?.[0]?.toUpperCase() ?? "?";
  return (
    <span
      aria-hidden
      className={`flex shrink-0 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary ${s.box} ${s.text} ${className}`}
    >
      {initial}
    </span>
  );
}
